import React, { useMemo } from 'react'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Autocomplete,
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useGetAccounts } from '../../api/user'

const validationSchema = yup.object().shape({
  companyName: yup.string().required('Company name is required'),
  ekt: yup
    .string()
    .nullable()
    .test('is-ekt', 'Select at least one: EKT, HWT, HTE, HTL, or HTB', function () {
      const { ekt, hwt, hte, htl, htb } = this.parent || {}
      return ekt || hwt || hte || htl || htb
    }),
  hwt: yup
    .string()
    .nullable()
    .test('is-hwt', 'Select at least one: EKT, HWT, HTE, HTL, or HTB', function () {
      const { ekt, hwt, hte, htl, htb } = this.parent || {}
      return ekt || hwt || hte || htl || htb
    }),
  hte: yup
    .string()
    .nullable()
    .test('is-hte', 'Select at least one: EKT, HWT, HTE, HTL, or HTB', function () {
      const { ekt, hwt, hte, htl, htb } = this.parent || {}
      return ekt || hwt || hte || htl || htb
    }),
  htl: yup
    .string()
    .nullable()
    .test('is-htl', 'Select at least one: EKT, HWT, HTE, HTL, or HTB', function () {
      const { ekt, hwt, hte, htl, htb } = this.parent || {}
      return ekt || hwt || hte || htl || htb
    }),
  htb: yup
    .string()
    .nullable()
    .test('is-htb', 'Select at least one: EKT, HWT, HTE, HTL, or HTB', function () {
      const { ekt, hwt, hte, htl, htb } = this.parent || {}
      return ekt || hwt || hte || htl || htb
    }),
})

const Form = ({ defaultValues, setOpen, open, api, refetch }) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues,
  })

  const { data = [], isLoading: accountsLoading } = useGetAccounts()

  const { companyNames, ektCompany, hwtCompany, hteCompany, htlCompany, htbCompany } =
    useMemo(() => {
      const companyNames = new Set()
      const companyGroups = {}

      data.forEach((item) => {
        companyNames.add(item.companyName)
        if (!companyGroups[item.htgCompany]) companyGroups[item.htgCompany] = []
        companyGroups[item.htgCompany].push(item)
      })

      return {
        companyNames: [...companyNames],
        ektCompany: companyGroups['HT EKOTAYAR SDN BHD'] || [],
        hwtCompany: companyGroups['HT TYREHUB SDN BHD'] || [],
        hteCompany: companyGroups['HT ECO BATTERY SDN BHD'] || [],
        htlCompany: companyGroups['HT LUBRICANT SDN BHD'] || [],
        htbCompany: companyGroups['HT BRAKES SDN BHD'] || [],
      }
    }, [data])

  const { enqueueSnackbar } = useSnackbar()
  const { mutateAsync, isLoading } = api()

  const onClose = () => {
    reset()
    setOpen(false)
  }

  const onSubmit = async (values) => {
    try {
      await mutateAsync(values)
      await refetch()
      enqueueSnackbar(`Successfully ${values.id ? 'updated' : 'created'}`, { variant: 'success' })
      onClose()
    } catch {
      enqueueSnackbar('Something went wrong. Please try again.', { variant: 'error' })
    }
  }

  return (
    <Dialog maxWidth='sm' open={open} fullWidth>
      <DialogTitle>Company Mapping</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name='companyName'
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    options={companyNames}
                    value={field.value || null}
                    onChange={(_, newValue) => field.onChange(newValue || '')}
                    loading={accountsLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Company Name'
                        variant='outlined'
                        fullWidth
                        error={!!errors.companyName}
                        helperText={errors.companyName?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name='ekt'
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    options={ektCompany}
                    getOptionLabel={({ companyName, companyAccountId }) =>
                      `${companyAccountId} - ${companyName}`
                    }
                    value={
                      ektCompany.find((option) => option.companyAccountId === field.value) || null
                    }
                    onChange={(_, newValue) =>
                      field.onChange(newValue ? newValue.companyAccountId : '')
                    }
                    loading={accountsLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='EKT'
                        variant='outlined'
                        fullWidth
                        error={!!errors.ekt}
                        helperText={errors.ekt?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name='hwt'
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    options={hwtCompany}
                    getOptionLabel={({ companyName, companyAccountId }) =>
                      `${companyAccountId} - ${companyName}`
                    }
                    value={
                      hwtCompany.find((option) => option.companyAccountId === field.value) || null
                    }
                    onChange={(_, newValue) =>
                      field.onChange(newValue ? newValue.companyAccountId : '')
                    }
                    loading={accountsLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='HWT'
                        variant='outlined'
                        fullWidth
                        error={!!errors.hwt}
                        helperText={errors.hwt?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name='hte'
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    options={hteCompany}
                    getOptionLabel={({ companyName, companyAccountId }) =>
                      `${companyAccountId} - ${companyName}`
                    }
                    value={
                      hteCompany.find((option) => option.companyAccountId === field.value) || null
                    }
                    onChange={(_, newValue) =>
                      field.onChange(newValue ? newValue.companyAccountId : '')
                    }
                    loading={accountsLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='HTE'
                        variant='outlined'
                        fullWidth
                        error={!!errors.hte}
                        helperText={errors.hte?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name='htl'
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    options={htlCompany}
                    getOptionLabel={({ companyName, companyAccountId }) =>
                      `${companyAccountId} - ${companyName}`
                    }
                    value={
                      htlCompany.find((option) => option.companyAccountId === field.value) || null
                    }
                    onChange={(_, newValue) =>
                      field.onChange(newValue ? newValue.companyAccountId : '')
                    }
                    loading={accountsLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='HTL'
                        variant='outlined'
                        fullWidth
                        error={!!errors.htl}
                        helperText={errors.htl?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name='htb'
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    options={htbCompany}
                    getOptionLabel={({ companyName, companyAccountId }) =>
                      `${companyAccountId} - ${companyName}`
                    }
                    value={
                      htbCompany.find((option) => option.companyAccountId === field.value) || null
                    }
                    onChange={(_, newValue) =>
                      field.onChange(newValue ? newValue.companyAccountId : '')
                    }
                    loading={accountsLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='HTB'
                        variant='outlined'
                        fullWidth
                        error={!!errors.htb}
                        helperText={errors.htb?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button variant='outlined' onClick={onClose}>
            CANCEL
          </Button>
          <Button type='submit' variant='contained' color='primary' disabled={isLoading}>
            SUBMIT
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default Form
